import { memo, useState } from "react";
import cc from "classcat";
import { modules } from "byzantine";
import { Localized, useLocalization } from "@fluent/react";
import { Accordion, Button, LoadingSkeleton, Row } from "@narmi/design_system";
import type Account from "byzantine/src/Account";
import type { LoanAutopayData } from "byzantine/src/dbbl/businessLogic/modules/loanPayment";
import styles from "./LoanAutopay.module.scss";
import LoanAutopayDialog from "./LoanAutopayDialog";

interface LoanAutopayCardProps {
  account: Account;
}

interface LoanAutopayCardEnrolledProps
  extends Pick<
    LoanAutopayData,
    "sourceAccountName" | "paymentAmount" | "paymentDate"
  > {
  canEdit: boolean;
  openAutopayDialog: () => void;
}

const { useLoanAutopay } = modules.loanPayment;

const LoanAutopayCardLoading = () => (
  <div
    className={cc([
      styles["card-container"],
      styles["card-container--loading"],
    ])}
  >
    <LoadingSkeleton
      isLoading
      showTitle
      lines={2}
      content="paragraph"
      testId="loading-skeleton"
    />
  </div>
);

const LoanAutopayCardUnenrolled = ({
  openAutopayDialog,
}: Pick<LoanAutopayCardEnrolledProps, "openAutopayDialog">) => {
  const { l10n } = useLocalization();
  return (
    <div className={styles["card-container"]}>
      <div>
        <div
          className={cc(["narmi-icon-refresh-cw", styles["icon-container"]])}
        />
      </div>

      <div className={styles["text-container"]}>
        <div className={styles["text-title"]}>
          <Localized id="loan-autopay-card-title" />
        </div>

        <div>
          <Localized id="loan-autopay-card-body-unenrolled" />
        </div>

        <div>
          <Button
            size="m"
            testId="autopay-button-setup"
            onClick={openAutopayDialog}
            label={l10n.getString("loan-autopay-card-button-setup")}
          />
        </div>
      </div>
    </div>
  );
};

const LoanAutopayCardEnrolled = ({
  paymentDate,
  paymentAmount,
  sourceAccountName,
  openAutopayDialog,
  canEdit,
}: LoanAutopayCardEnrolledProps) => {
  const { l10n } = useLocalization();
  const [isEditDisclaimerExpanded, setIsEditDisclaimerExpanded] =
    useState<boolean>(false);
  const editDisclaimerIcon = isEditDisclaimerExpanded
    ? "narmi-icon-chevron-up"
    : "narmi-icon-chevron-down";

  return (
    <div className={styles["card-container"]}>
      <div>
        <div
          className={cc(["narmi-icon-refresh-cw", styles["icon-container"]])}
        />
      </div>

      <div className={styles["text-container"]}>
        <Row alignItems="center">
          <Row.Item shrink className={styles["text-title"]}>
            <Localized id="loan-autopay-card-title" />
          </Row.Item>

          <Row.Item
            className={cc([
              styles["text-title"],
              styles["active-text-container"],
            ])}
          >
            <div className={styles["active-dot"]} />
            <Localized id="loan-autopay-card-title-active-badge" />
          </Row.Item>
        </Row>

        <Localized
          id="loan-autopay-card-body-enrolled"
          elems={{ bold: <strong /> }}
          vars={{
            paymentDate,
            hasPaymentAmount: `${!!paymentAmount}`,
            paymentAmount: paymentAmount || "",
          }}
        >
          <div />
        </Localized>

        <Row alignItems="center" gapSize="s">
          <Row.Item shrink className={cc(["narmi-icon-shopping-bag"])} />
          <Row.Item className={styles["subtitle-text"]}>
            <Localized
              id="loan-autopay-card-source-account-display"
              vars={{ sourceAccountName }}
            />
          </Row.Item>
        </Row>

        {canEdit ? (
          <div>
            <Button
              size="xs"
              onClick={openAutopayDialog}
              label={l10n.getString("loan-autopay-card-button-manage")}
              kind="secondary"
            />
          </div>
        ) : (
          <div>
            <Accordion
              isOpen={isEditDisclaimerExpanded}
              onUserToggle={setIsEditDisclaimerExpanded}
              renderSummary={() => (
                <Row
                  alignItems="center"
                  gapSize="s"
                  className={styles["subtitle-text"]}
                >
                  <Localized id="loan-autopay-card-button-manage" />
                  <Row.Item shrink className={editDisclaimerIcon} />
                </Row>
              )}
            >
              <div className={styles["disclaimer-text"]}>
                <Localized id="loan-autopay-dialog-disclaimer" />
              </div>
            </Accordion>
          </div>
        )}
      </div>
    </div>
  );
};

const LoanAutopayCard = ({ account }: LoanAutopayCardProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const { isLoanAutopayEnabled, isLoading, autopayData } = useLoanAutopay(
    account.id
  );
  const onUserDismissHandler = () => setIsDialogOpen(false);
  const openAutopayDialogHandler = () => {
    setIsDialogOpen(true);
  };

  if (!isLoanAutopayEnabled || account.product_type !== "loan") {
    return null;
  }

  if (isLoading) {
    return <LoanAutopayCardLoading />;
  }

  const card = !autopayData ? (
    <LoanAutopayCardUnenrolled openAutopayDialog={openAutopayDialogHandler} />
  ) : (
    <LoanAutopayCardEnrolled
      openAutopayDialog={openAutopayDialogHandler}
      sourceAccountName={autopayData.sourceAccountName}
      paymentAmount={autopayData.paymentAmount}
      paymentDate={autopayData.paymentDate}
      canEdit={autopayData.canEdit}
    />
  );

  return (
    <>
      <LoanAutopayDialog
        isOpen={isDialogOpen}
        onUserDismiss={onUserDismissHandler}
        loanAccountId={account.id}
        loanAccountNumber={account.number}
        currentSourceAccountId={autopayData?.sourceAccountId || undefined}
        paymentAmount={autopayData?.paymentAmount}
        paymentDate={autopayData?.paymentDate}
      />
      {card}
    </>
  );
};

export default memo(LoanAutopayCard);
